import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { mapToBase } from "go-core/api/searchSelectService";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import ReportData from "go-report/reports";
import { reportParamsConverter } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";

interface State {
	resource?: Record<string, any>;
	customFields: CustomFieldTemplateApi[];
}

const listName = "ADMIN_COMPANY_REPORT";
const resourceType = "COMPANY_REPORT";

const Report = ({ resource, customFields }: State) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const getCFOptions = (slug: string) => {
		const cfOptions = customFields.filter((x) => x.slug === slug)[0];
		if (cfOptions === undefined) return undefined;

		const record: Record<string, string> = cfOptions.options.reduce((acc, obj) => {
			acc[obj.value] = obj.value;
			return acc;
		}, {});
		return record;
	};
	const parseFiltersCF = (options: any) => {
		const id = options.id;
		const cfOptions = getCFOptions(id);
		if (cfOptions === undefined) return { ...options, type: "text" };
		return { ...options, type: "list", listOptions: cfOptions };
	};

	let config = {
		reportConfigId: "admin_company_report",
		aggregatePrefix: "summary",
		selectedGroups: ["NONE"],
		showPercent: true,
		groups: [
			{
				id: "NAME",
				name: t("lib:common.word.name"),
			},
			{
				id: "PARTNERS",
				name: t("common.word.partners"),
			},
			{
				id: "APPLICATIONS",
				name: t("common.word.applications"),
			},
			{
				id: "ACCESSES",
				name: t("modules.company_report.field.accesses.title"),
			},
			{
				id: "SUBSCRIPTIONS",
				name: t("common.word.subscriptions"),
			},
			{
				id: "SUBSCRIPTION_STATUS",
				name: t("modules.company_report.field.subscription_status.title"),
			},
			{
				id: "CANCELLATION_REASON",
				name: t("common.word.cancellation_reason"),
			},
			{
				id: "CANCELLATION_REASON_DETAILS",
				name: t("modules.company_report.field.cancellation_reason_details.title"),
			},
			{
				id: "SOURCE",
				name: t("lib:common.word.source"),
			},
			{
				id: "FIRST_BILLED_DATE",
				name: t("modules.company_report.field.first_billed_date.title"),
			},
			{
				id: "FIRST_PAID_DATE",
				name: t("common.word.first_paid_at"),
			},
			{
				id: "REACTIVATED_DATE",
				name: t("modules.company_report.field.reactivated_date.title"),
			},
			{
				id: "CANCELLED_AT",
				name: t("common.word.cancelled_at"),
			},
			{
				id: "BILLING_PERIOD",
				name: t("common.word.license_type"),
			},
			{
				id: "COMPANY_ID",
				name: t("common.word.company_id"),
			},
			{
				id: "EXTERNAL_ID",
				name: t("modules.company_report.field.external_id.title"),
			},
			{
				id: "TAX_ID_NO",
				name: t("lib:common.word.tax_id_no.title"),
			},
			{
				id: "LICENSE_EXPIRES_AT",
				name: t("modules.company_report.field.license_expires_at.title"),
			},
			{
				id: "LICENSE_EXPIRES_AT_DAY",
				name: t("modules.company_report.field.license_expires_at_day.title"),
			},
			{
				id: "LICENSE_EXPIRES_AT_MONTH",
				name: t("modules.company_report.field.license_expires_at_month.title"),
			},
			{
				id: "LICENSE_EXPIRES_AT_YEAR",
				name: t("modules.company_report.field.license_expires_at_year.title"),
			},
			{
				id: "CUSTOMER_TYPE",
				name: t("lib:common.word.type"),
			},
			{
				id: "KEEPER_TYPE",
				name: t("modules.company_report.field.keeper_type.title"),
			},
			{
				id: "GOPOS_VERSION",
				name: t("modules.company_report.field.gopos_version.title"),
			},
			{
				id: "SEASONAL",
				name: t("modules.company_report.field.seasonal.title"),
			},
			{
				id: "GOPOS_LICENSE_LEVEL",
				name: t("modules.company_report.field.gopos_license_level.title"),
			},
		],
		groupsInfo: {
			BILLING_PERIOD: (groupByValue: Record<string, any>) => {
				return t(`enums.provisions.period.${groupByValue.name}`);
			},
			SUBSCRIPTION_STATUS: (groupByValue: Record<string, any>) => {
				if (groupByValue.name === "IN_TRIAL") return t("enums.licenses.TRIAL", { ns: "admin" });
				return t(`enums.licenses.${groupByValue.name}`);
			},
			NAME: (groupByValue: Record<string, any>) => {
				const link = `/admin/organizations?search=${groupByValue.name}`;
				return (
					<Link to={link} rel="noreferrer" target="_blank">
						{groupByValue.name}
					</Link>
				);
			},
			COMPANY_ID: (groupByValue: Record<string, any>) => {
				const filter = btoa(unescape(encodeURIComponent(`company_id|e=${groupByValue.name}`)));
				const link = `/admin/organizations?f=${NEW_WAY_TO_ENCODING_FILTER_SIGN + filter}`;
				return (
					<Link to={link} rel="noreferrer" target="_blank">
						{groupByValue.name}
					</Link>
				);
			},
		},
		columns: [
			{
				id: "count",
				name: t("common.word.amount"),
			},
			{
				id: "mrr",
				name: t("common.word.mrr"),
			},
			{
				id: "mrr_support",
				name: t("modules.company_report.field.mrr_support.title"),
			},
			{
				id: "mrr_license",
				name: t("modules.company_report.field.mrr_license.title"),
			},
		],
		selectedColumns: ["count", "mrr", "mrr_license", "mrr_support"],
		filters: [
			{
				id: "first_billed_date",
				name: t("modules.company_report.field.first_billed_date.title"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "first_paid_date",
				name: t("common.word.first_paid_at"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "reactivated_date",
				name: t("modules.company_report.field.reactivated_date.title"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "cancelled_at",
				name: t("common.word.cancelled_at"),
				type: "date",
				extraOptions: ["a", "u"],
			},
			{
				id: "subscription_status",
				name: t("modules.company_report.field.subscription_status.title"),
				type: "list",
				options: {
					ACTIVE: t("enums.licenses.ACTIVE"),
					IN_TRIAL: t("enums.licenses.TRIAL"),
					CANCELLED: t("enums.licenses.CANCELLED"),
				},
			},
			parseFiltersCF({
				id: "cancellation_reason",
				name: t("common.word.cancellation_reason"),
			}),
			{
				id: "cancellation_reason_details",
				name: t("modules.company_report.field.cancellation_reason_details.title"),
				type: "text",
			},
			{
				id: "name",
				name: t("lib:common.word.name"),
				type: "text",
			},
			{
				id: "company_id",
				name: t("common.word.company_id"),
				type: "text",
			},
			{
				id: "external_id",
				name: t("modules.company_report.field.external_id.title"),
				type: "text",
			},
			{
				id: "tax_id_no",
				name: t("lib:common.word.tax_id_no.title"),
				type: "text",
			},
			{
				id: "license_expires_at",
				name: t("modules.company_report.field.license_expires_at.title"),
				type: "date",
			},
			parseFiltersCF({
				id: "source",
				name: t("lib:common.word.source"),
			}),
			parseFiltersCF({
				id: "customer_type",
				name: t("lib:common.word.type"),
			}),
			{
				id: "partners",
				name: t("common.word.partners"),
				type: "text",
			},
			{
				id: "applications",
				name: t("common.word.applications"),
				type: "text",
			},
			{
				id: "subscriptions",
				name: t("common.word.subscriptions"),
				type: "text",
			},
			{
				id: "accesses",
				name: t("modules.company_report.field.accesses.title"),
				type: "text",
			},
			{
				id: "billing_period",
				name: t("common.word.license_type"),
				type: "list",
				options: {
					MONTHLY: t("enums.provisions.period.MONTHLY"),
					YEARLY: t("enums.provisions.period.YEARLY"),
				},
			},
			parseFiltersCF({
				id: "keeper_type",
				name: t("modules.company_report.field.keeper_type.title"),
			}),
			parseFiltersCF({
				id: "gopos_version",
				name: t("modules.company_report.field.gopos_version.title"),
			}),
			parseFiltersCF({
				id: "seasonal",
				name: t("modules.company_report.field.seasonal.title"),
			}),
			parseFiltersCF({
				id: "gopos_license_level",
				name: t("modules.company_report.field.gopos_license_level.title"),
			}),
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		segments: [
			{
				id: "all",
				name: `${t("common.word.all", { ns: "lib" })}`,
				slug: "all",
				columns: ["count", "mrr", "mrr_support", "mrr_license"],
				filters: [],
				groups: [],
			},
		],
		fetch: async (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				adminApi.getCompaniesReport(reportParamsConverter(params), { cancelToken: sourceToken?.token }),
			]);
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentContextValue.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.company_report.header.title"),
			filename: t("modules.company_report.field.export_config.filename"),
		},
		doNotUseDefaultDateRangeInFilters: true,
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const AdminCompaniesReportPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const [customFields, setCustomFields] = useState<CustomFieldTemplateApi[]>([]);
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { addFlash } = useFlash();

	const fetchCF = async () => {
		try {
			const f = mapToBase("", {
				slug: "keeper_type,source,cancellation_reason,customer_type,gopos_version,seasonal",
			});
			const res = await adminApi.getCustomFields({ f, include: "options" });
			setCustomFields(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.company_report.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, [])));
		fetchCF();
	}, []);

	return (
		<div className={"content"}>
			{!isMobile && <Header title={t("modules.company_report.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<Report resource={resource} customFields={customFields} />
			</Suspense>
		</div>
	);
};

export default AdminCompaniesReportPage;
